import React from 'react';
import Slider from 'react-slick';
import classNames from 'classnames';

import RcTooltip from 'rc-tooltip';

import map from 'lodash/map';
import noop from 'lodash/noop';

import {
  DownloadIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FullscreenIcon
} from 'zoomforth_component_lib/external_modern_icons.jsx';

import {
  VisuallyHiddenSpan
} from 'zoomforth_codesbane_viewer/component_lib/visually_hidden.jsx';

import Carousel from 'zoomforth_codesbane_viewer/component_lib/carousel.jsx';

import {
  ConditionalSkipLinkForElement
} from 'zoomforth_codesbane_viewer/component_lib/skip_link_for_element.jsx';

import PresentationWrapper from './presentation_wrapper.jsx';

import SiteViewerVersionComponentSelector
  from 'zoomforth_codesbane_viewer/component_lib/site_viewer_version_component_selector_container.jsx';


const PresentationUi_ViewerVersion1 = ({
  height,
  slides,
  slickSliderRef,
  currentSlideIdx,
  displayFilename,
  canDownloadAsset,
  onClick,
  onGoToSlideClick,
  onNextClick,
  onPrevClick,
  onDownloadClick,
  onFullscreenClick,
  onAfterChange
})=> (
  <PresentationWrapper
    height={height}
    slides={slides}
    slickSliderRef={slickSliderRef}
    currentSlideIdx={currentSlideIdx}
    displayFilename={displayFilename}
    canDownloadAsset={canDownloadAsset}
    onClick={onClick}
    onGoToSlideClick={onGoToSlideClick}
    onNextClick={onNextClick}
    onPrevClick={onPrevClick}
    onDownloadClick={onDownloadClick}
    onFullscreenClick={onFullscreenClick}
  >

    <Slider
      ref={slickSliderRef}
      arrows={false}
      infinite={false}
      afterChange={onAfterChange}
    >

      {
        map(slides, (imgSrc, idx) => (
          <div
            key={idx}
            style={{ backgroundImage: `url("${imgSrc}")` }}
          />
        ))
      }

    </Slider>
  </PresentationWrapper>
);

const PresentationUi_ViewerVersion2 = ({
  height,
  slides,
  currentSlideIdx,
  displayFilename,
  canDownloadAsset,
  isFullscreenEnabled,
  shouldIncludeSkipLink,
  onClick,
  onNextClick,
  onPrevClick,
  onAfterChange,
  onDownloadClick,
  onGoToSlideClick,
  onFullscreenClick
})=> {
  const totalSlides = slides.length;
  const currentSlideNum = currentSlideIdx + 1;
  const isFirstSlide = currentSlideIdx === 0;
  const isLastSlide = currentSlideIdx === (totalSlides - 1);

  return (
    <div
      role="region"
      style={{ height }}
      aria-label={`Presentation of ${displayFilename}`}
      className={classNames('presentation-wrapper accessible-presentation', {
        'is-fullscreen-enabled': isFullscreenEnabled
      })}
      onClick={onClick}
    >
      <ConditionalSkipLinkForElement
        skipLinkText="Skip presentation"
        shouldShowSkipLink={shouldIncludeSkipLink}
      >
        <div className="accessible-presentation-nav accessible-presentation-top">
          <h2 className="presentation-title">
            { displayFilename }
          </h2>

          <div className="accessible-presentation-download-wrapper">
            <button
              type="button"
              disabled={!canDownloadAsset}
              aria-disabled={canDownloadAsset ? null : 'true'}
              onClick={canDownloadAsset ? onDownloadClick : noop}
            >
              <VisuallyHiddenSpan>
                Download { displayFilename }
              </VisuallyHiddenSpan>
              <DownloadIcon className="download-btn" onClick={onDownloadClick} />
            </button>
          </div>
        </div>

        <div className="accessible-presentation-nav accessible-presentation-bottom">
          <div className="accessible-presentation-controls">
            <button
              type="button"
              disabled={isFirstSlide}
              aria-disabled={isFirstSlide ? 'true' : null}
              className={classNames('prev-btn', { disabled: isFirstSlide })}
              onClick={
                isFirstSlide ?
                  noop
                :
                  ()=> {
                    onPrevClick();
                    onAfterChange(currentSlideIdx - 1);
                  }
              }
            >
              <VisuallyHiddenSpan>
                View previous slide
              </VisuallyHiddenSpan>
              <ChevronLeftIcon />
            </button>

            <div className="slide-counter">
              { currentSlideNum } of { totalSlides }
            </div>

            <button
              type="button"
              disabled={isLastSlide}
              aria-disabled={isLastSlide ? 'true' : null}
              className={classNames('next-btn', { disabled: isLastSlide })}
              onClick={
                isLastSlide ?
                  noop
                :
                  ()=> {
                    onNextClick();
                    onAfterChange(currentSlideIdx + 1);
                  }
              }
            >
              <VisuallyHiddenSpan>
                View next slide
              </VisuallyHiddenSpan>
              <ChevronRightIcon />
            </button>
          </div>

          <div className="accessible-presentation-progress-bar">
            {
              map(slides, (imgSrc, idx) => (
                <RcTooltip 
                  key={idx}
                  placement="top"
                  popupClassName="presentation-tipsy"
                  trigger={['hover', 'focus']}
                  overlay={
                    <div className="presentation-progress-tooltip">
                      <img src={imgSrc} alt={`slide ${idx+1} preview`} />
                      <div>Slide { idx + 1 }</div>
                    </div>
                  }
                  mouseEnterDelay={0}
                  mouseLeaveDelay={0}
                >
                  <button
                    type="button"
                    aria-current={idx === currentSlideIdx ? 'true' : null}
                    style={{ width: `${(1 / totalSlides) * 100}%` }}
                    className={classNames('accessible-progress-step', {
                      'is-current': idx === currentSlideIdx,
                      active: currentSlideIdx >= idx
                    })}
                    onKeyDown={(e)=> {
                      if (e.keyCode === 37 && !isFirstSlide) {
                        // previous
                        onPrevClick();
                        onAfterChange(currentSlideIdx - 1);
                      } else if (e.keyCode === 39 && !isLastSlide) {
                        // next
                        onNextClick();
                        onAfterChange(currentSlideIdx + 1);
                      }
                    }}
                    onClick={() => {
                      onGoToSlideClick(idx);
                      onAfterChange(idx);
                    }}
                  >
                    <VisuallyHiddenSpan>
                      Slide { idx + 1 }
                    </VisuallyHiddenSpan>
                  </button>
                </RcTooltip>
              ))
            }
          </div>

          <div className="accessible-presentation-fullscreen-wrapper">
            <button type="button" className="fullscreen-btn" onClick={onFullscreenClick}>
              <VisuallyHiddenSpan>
                Open fullscreen
              </VisuallyHiddenSpan>
              <FullscreenIcon />
            </button>
          </div>
        </div>

        <Carousel 
          currentSlideIdx={currentSlideIdx}
          viewportClassName="accessible-presentation-viewport"
        >
          {
            map(slides, (imgSrc, idx) => (
              <div key={idx} id={`slide${idx+1}`}>
                <img src={imgSrc} alt={`Slide ${idx + 1} image`} />
              </div>
            ))
          }
        </Carousel>

      </ConditionalSkipLinkForElement>
    </div>
  );
};

const PresentationUi = (props)=> (
  <SiteViewerVersionComponentSelector>
    <PresentationUi_ViewerVersion1 forSiteViewerVersion={1} { ...props } />
    <PresentationUi_ViewerVersion2 forSiteViewerVersion={2} { ...props } />
  </SiteViewerVersionComponentSelector>
);

export default PresentationUi;
